import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ZoomIn, ZoomOut, Maximize2, MousePointer, Hand } from 'lucide-react';
import FlowNode from './FlowNode';

const FlowCanvas = ({
    nodes,
    connections,
    selectedNodeId,
    onNodeSelect,
    onNodeUpdate,
    onNodeDelete,
    onNodeDuplicate,
    onConnectionCreate,
    onConnectionDelete,
}) => {
    const canvasRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 50, y: 50 });
    const [isDragging, setIsDragging] = useState(false);
    const [isPanning, setIsPanning] = useState(false);
    const [draggedNode, setDraggedNode] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const [connecting, setConnecting] = useState(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [tool, setTool] = useState('select');

    // Convert screen coordinates to canvas logical coordinates
    const screenToCanvas = useCallback((clientX, clientY) => {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return { x: 0, y: 0 };

        return {
            x: (clientX - rect.left - offset.x) / scale,
            y: (clientY - rect.top - offset.y) / scale,
        };
    }, [offset, scale]);

    // Handle zoom
    const handleWheel = useCallback((e) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? 0.9 : 1.1;
            setScale(s => Math.min(Math.max(s * delta, 0.25), 2));
        }
    }, []);

    // Handle canvas click (deselect)
    const handleCanvasClick = (e) => {
        if (e.target === canvasRef.current || e.target.classList.contains('canvas-grid')) {
            onNodeSelect?.(null);
            if (connecting) {
                setConnecting(null);
            }
        }
    };

    // Handle node drag start
    const handleNodeMouseDown = useCallback((e, nodeId) => {
        if (tool === 'pan') return;
        if (e.target.closest('.node-handle') || e.target.closest('.node-action')) return;

        const node = nodes.find(n => n.id === nodeId);
        if (!node) return;

        const canvasPos = screenToCanvas(e.clientX, e.clientY);

        setDraggedNode(nodeId);
        setDragOffset({
            x: canvasPos.x - node.position.x,
            y: canvasPos.y - node.position.y,
        });
        setIsDragging(true);
        onNodeSelect?.(nodeId);
    }, [nodes, screenToCanvas, tool, onNodeSelect]);

    // Handle mouse move
    const handleMouseMove = useCallback((e) => {
        const canvasPos = screenToCanvas(e.clientX, e.clientY);
        setMousePos(canvasPos);

        if (isDragging && draggedNode) {
            const newX = canvasPos.x - dragOffset.x;
            const newY = canvasPos.y - dragOffset.y;

            const snappedX = Math.round(newX / 20) * 20;
            const snappedY = Math.round(newY / 20) * 20;

            onNodeUpdate?.(draggedNode, {
                position: { x: Math.max(0, snappedX), y: Math.max(0, snappedY) }
            });
        }

        if (isPanning) {
            setOffset({
                x: e.clientX - panStart.x,
                y: e.clientY - panStart.y,
            });
        }
    }, [isDragging, isPanning, draggedNode, dragOffset, panStart, onNodeUpdate, screenToCanvas]);

    // Handle mouse up
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        setDraggedNode(null);
        setIsPanning(false);
    }, []);

    // Handle pan start
    const handlePanStart = useCallback((e) => {
        if (tool === 'pan' || e.button === 1 || (e.button === 0 && e.altKey)) {
            setIsPanning(true);
            setPanStart({
                x: e.clientX - offset.x,
                y: e.clientY - offset.y,
            });
        }
    }, [tool, offset]);

    // Start connection from output handle
    const handleStartConnection = useCallback((nodeId, outputIndex) => {
        setConnecting({ nodeId, outputIndex });
    }, []);

    // Complete connection
    const handleConnect = useCallback((fromNodeId, outputIndex, toNodeId) => {
        if (fromNodeId === toNodeId) {
            setConnecting(null);
            return;
        }

        const exists = connections.some(
            c => c.from === fromNodeId && c.fromOutput === outputIndex && c.to === toNodeId
        );

        if (!exists) {
            onConnectionCreate?.({
                id: `conn_${Date.now()}`,
                from: fromNodeId,
                fromOutput: outputIndex,
                to: toNodeId,
            });
        }

        setConnecting(null);
    }, [connections, onConnectionCreate]);

    // Cancel connection on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && connecting) {
                setConnecting(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [connecting]);

    // Node dimensions
    const NODE_WIDTH = 264;
    const HEADER_HEIGHT = 40;

    // Calculate connection path using cubic bezier
    const getConnectionPath = (fromNode, fromOutput, toNode) => {
        if (!fromNode || !toNode) return '';

        const fromX = fromNode.position.x + NODE_WIDTH;
        const fromY = fromNode.position.y + HEADER_HEIGHT + (fromOutput * 32);
        const toX = toNode.position.x;
        const toY = toNode.position.y + HEADER_HEIGHT;

        // Calculate control points for smooth curve
        const dx = Math.abs(toX - fromX);
        const controlOffset = Math.max(dx * 0.5, 50);

        return `M ${fromX} ${fromY} C ${fromX + controlOffset} ${fromY}, ${toX - controlOffset} ${toY}, ${toX} ${toY}`;
    };

    // Get temp connection path while dragging
    const getTempConnectionPath = () => {
        if (!connecting) return '';

        const fromNode = nodes.find(n => n.id === connecting.nodeId);
        if (!fromNode) return '';

        const fromX = fromNode.position.x + NODE_WIDTH;
        const fromY = fromNode.position.y + HEADER_HEIGHT + (connecting.outputIndex * 32);
        const toX = mousePos.x;
        const toY = mousePos.y;

        // Calculate control points
        const dx = Math.abs(toX - fromX);
        const controlOffset = Math.max(dx * 0.5, 50);

        // Always curve to the right first, then to target
        return `M ${fromX} ${fromY} C ${fromX + controlOffset} ${fromY}, ${toX - controlOffset} ${toY}, ${toX} ${toY}`;
    };

    // Zoom controls
    const zoomIn = () => setScale(s => Math.min(s * 1.2, 2));
    const zoomOut = () => setScale(s => Math.max(s * 0.8, 0.25));
    const resetView = () => {
        setScale(1);
        setOffset({ x: 50, y: 50 });
    };

    // Register wheel event
    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            canvas.addEventListener('wheel', handleWheel, { passive: false });
        }
        return () => {
            if (canvas) {
                canvas.removeEventListener('wheel', handleWheel);
            }
        };
    }, [handleWheel]);

    return (
        <div className="relative flex-1 bg-[#050810] overflow-hidden">
            {/* Toolbar */}
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-[#0d1424]/90 backdrop-blur border border-white/10 rounded-lg p-1">
                <button
                    onClick={() => setTool('select')}
                    className={`p-2 rounded-lg transition ${
                        tool === 'select'
                            ? 'bg-[#ff4d4d]/20 text-[#ff4d4d]'
                            : 'text-white/50 hover:text-white hover:bg-white/5'
                    }`}
                    title="Select (V)"
                >
                    <MousePointer size={18} />
                </button>
                <button
                    onClick={() => setTool('pan')}
                    className={`p-2 rounded-lg transition ${
                        tool === 'pan'
                            ? 'bg-[#ff4d4d]/20 text-[#ff4d4d]'
                            : 'text-white/50 hover:text-white hover:bg-white/5'
                    }`}
                    title="Pan (H)"
                >
                    <Hand size={18} />
                </button>
                <div className="w-px h-6 bg-white/10" />
                <button
                    onClick={zoomOut}
                    className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition"
                    title="Zoom Out"
                >
                    <ZoomOut size={18} />
                </button>
                <span className="text-sm text-white/50 w-12 text-center">
                    {Math.round(scale * 100)}%
                </span>
                <button
                    onClick={zoomIn}
                    className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition"
                    title="Zoom In"
                >
                    <ZoomIn size={18} />
                </button>
                <button
                    onClick={resetView}
                    className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition"
                    title="Reset View"
                >
                    <Maximize2 size={18} />
                </button>
            </div>

            {/* Canvas */}
            <div
                ref={canvasRef}
                className={`w-full h-full ${tool === 'pan' || isPanning ? 'cursor-grab' : 'cursor-default'} ${isPanning ? 'cursor-grabbing' : ''}`}
                onClick={handleCanvasClick}
                onMouseDown={handlePanStart}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                {/* Grid Background */}
                <div
                    className="canvas-grid absolute inset-0"
                    style={{
                        backgroundImage: `
                            linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px)
                        `,
                        backgroundSize: `${20 * scale}px ${20 * scale}px`,
                        backgroundPosition: `${offset.x}px ${offset.y}px`,
                    }}
                />

                {/* Transform container for both connections and nodes */}
                <div
                    className="absolute inset-0"
                    style={{
                        transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                        transformOrigin: '0 0',
                    }}
                >
                    {/* Connections SVG */}
                    <svg
                        className="absolute inset-0 overflow-visible pointer-events-none"
                        style={{ width: '100%', height: '100%' }}
                    >
                        <defs>
                            <marker
                                id="arrowhead"
                                markerWidth="10"
                                markerHeight="7"
                                refX="9"
                                refY="3.5"
                                orient="auto"
                            >
                                <polygon
                                    points="0 0, 10 3.5, 0 7"
                                    fill="rgba(255,77,77,0.8)"
                                />
                            </marker>
                            <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="rgba(255,77,77,0.8)" />
                                <stop offset="100%" stopColor="rgba(255,77,77,0.4)" />
                            </linearGradient>
                        </defs>

                        {/* Existing connections */}
                        {connections.map(conn => {
                            const fromNode = nodes.find(n => n.id === conn.from);
                            const toNode = nodes.find(n => n.id === conn.to);
                            const path = getConnectionPath(fromNode, conn.fromOutput || 0, toNode);

                            return (
                                <g key={conn.id}>
                                    {/* Clickable area */}
                                    <path
                                        d={path}
                                        fill="none"
                                        stroke="transparent"
                                        strokeWidth="12"
                                        className="pointer-events-auto cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onConnectionDelete?.(conn.id);
                                        }}
                                    />
                                    {/* Visible line */}
                                    <path
                                        d={path}
                                        fill="none"
                                        stroke="url(#connectionGradient)"
                                        strokeWidth="2.5"
                                        markerEnd="url(#arrowhead)"
                                        className="pointer-events-none"
                                    />
                                </g>
                            );
                        })}

                        {/* Temp connection while dragging */}
                        {connecting && (
                            <path
                                d={getTempConnectionPath()}
                                fill="none"
                                stroke="#ff4d4d"
                                strokeWidth="2.5"
                                strokeDasharray="8,4"
                                className="pointer-events-none"
                                style={{ filter: 'drop-shadow(0 0 4px rgba(255,77,77,0.5))' }}
                            />
                        )}
                    </svg>

                    {/* Nodes */}
                    {nodes.map(node => (
                        <div
                            key={node.id}
                            onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                        >
                            <FlowNode
                                node={node}
                                isSelected={selectedNodeId === node.id}
                                isDragging={draggedNode === node.id}
                                onSelect={onNodeSelect}
                                onDelete={onNodeDelete}
                                onDuplicate={onNodeDuplicate}
                                onStartConnection={handleStartConnection}
                                onConnect={handleConnect}
                                isConnecting={!!connecting}
                                connectingFrom={connecting}
                                scale={1}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Connection mode indicator */}
            {connecting && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#ff4d4d] text-white rounded-full text-sm font-medium shadow-lg flex items-center gap-3">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    Click on the RED circle of another step to connect
                    <span className="text-white/70">• Esc to cancel</span>
                </div>
            )}

            {/* Help hints */}
            <div className="absolute bottom-4 right-4 text-xs text-white/30">
                Scroll to zoom • Hold Alt to pan
            </div>
        </div>
    );
};

export default FlowCanvas;
